// SSE 연결 관리를 위한 싱글톤 클래스
class SSEManager {
  constructor() {
    if (SSEManager.instance) {
      return SSEManager.instance;
    }
    SSEManager.instance = this;
    this.eventSource = null;
    this.connectionInfo = null;
    this.connectionCheckTimer = null;
    this.reconnectCount = 0;
    this.maxReconnectAttempts = 5;
    this.lastConnectionTime = null;
  }

  connect(url, handlers) {
    // 기존 연결이 있고 같은 URL이면 중복 연결 방지
    if (this.eventSource && this.connectionInfo?.url === url && this.isConnected()) {
      return;
    }

    // 기존 연결이 있으면 먼저 정리
    this.disconnect();

    try {
      this.eventSource = new EventSource(url);
      this.connectionInfo = { url, handlers };

      // 기본 이벤트 핸들러 설정
      this.eventSource.onopen = () => {
        this.lastConnectionTime = Date.now();
        this.reconnectCount = 0; // 연결 성공 시 재연결 카운트 리셋
        
        // 연결 확인 타이머가 있다면 정리 (연결 성공했으므로)
        if (this.connectionCheckTimer) {
          clearTimeout(this.connectionCheckTimer);
          this.connectionCheckTimer = null;
        }
      };

      // 에러 핸들러
      this.eventSource.onerror = (error) => {
        console.error('❌ SSE 연결 오류:', error);
        
        // 연결 직후 바로 에러가 발생하는 경우 (서버에서 즉시 종료)
        const timeSinceConnection = this.lastConnectionTime ? Date.now() - this.lastConnectionTime : 0;
        if (timeSinceConnection < 1000) {
          console.warn('⚠️ 서버에서 연결 직후 즉시 종료함 (연결 후', timeSinceConnection + 'ms)');
          this.reconnectCount++;
        }

        // EventSource 상태별 처리
        if (this.eventSource) {
          switch (this.eventSource.readyState) {
            case EventSource.CONNECTING:
              if (this.reconnectCount >= this.maxReconnectAttempts) {
                console.error('🚫 SSE 재연결 시도 한도 초과 (' + this.reconnectCount + '/' + this.maxReconnectAttempts + ')');
                console.error('🔧 서버 측에서 연결을 계속 종료하고 있습니다. 서버 로그를 확인해주세요.');
                this.disconnect();
              } 
              break;
            case EventSource.OPEN:
              // 연결은 유지되고 있으므로 특별한 처리 없음
              break;
            case EventSource.CLOSED:
              this.disconnect();
              break;
            default:
              break;
          }
        }
      };

      // 커스텀 이벤트 핸들러 등록
      if (handlers) {
        Object.entries(handlers).forEach(([event, handler]) => {
          this.eventSource.addEventListener(event, handler);
        });
      }

      // 페이지 언로드 시 연결 정리를 위한 이벤트 리스너
      window.addEventListener('beforeunload', this.disconnect.bind(this));
      window.addEventListener('pagehide', this.disconnect.bind(this));

    } catch (error) {
      console.error('SSE 연결 생성 실패:', error);
      this.disconnect();
    }
  }

  disconnect() {
    if (this.eventSource) {
      
      // 연결 확인 타이머가 있다면 정리
      if (this.connectionCheckTimer) {
        clearTimeout(this.connectionCheckTimer);
        this.connectionCheckTimer = null;
      }

      // 등록된 모든 이벤트 리스너 제거
      if (this.connectionInfo?.handlers) {
        Object.entries(this.connectionInfo.handlers).forEach(([event, handler]) => {
          this.eventSource.removeEventListener(event, handler);
        });
      }

      // 연결 상태 확인 후 종료
      if (this.eventSource.readyState !== EventSource.CLOSED) {
        this.eventSource.close();
      }
      
      this.eventSource = null;
      this.connectionInfo = null;
      this.reconnectCount = 0; // 재연결 카운트 리셋
      this.lastConnectionTime = null;
      
    }

    // 페이지 언로드 이벤트 리스너 제거
    window.removeEventListener('beforeunload', this.disconnect.bind(this));
    window.removeEventListener('pagehide', this.disconnect.bind(this));
  }

  isConnected() {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }

  getReadyStateText(state) {
    switch (state) {
      case EventSource.CONNECTING:
        return 'CONNECTING (0)';
      case EventSource.OPEN:
        return 'OPEN (1)';
      case EventSource.CLOSED:
        return 'CLOSED (2)';
      default:
        return `UNKNOWN (${state})`;
    }
  }
}

export default new SSEManager();
